import type { CellContext, ColumnDef } from "@tanstack/react-table";
import { EditIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { m } from "@vega-core/paraglide/messages";
import { compareDatesAsc } from "@vega-core/utils/compareDates";

import {
  formatDpi,
  formatIrr,
  formatIrrDelta,
  formatMoic,
  formatPeriodLabel,
  formatRatioDelta,
  formatTvpi,
  type HistoryRow,
} from "../../../utils/formatFinancialReturns";

type DeltaCellProps = {
  value: string;
  delta: number | undefined;
  formatDelta: (delta: number) => string;
  suppressDelta?: boolean;
};

function DeltaCell({
  value,
  delta,
  formatDelta,
  suppressDelta,
}: DeltaCellProps) {
  const showDelta = !suppressDelta && delta !== undefined && delta !== 0;

  return (
    <span className="flex items-center gap-1.5 body-base-regular">
      <span>{value}</span>
      {showDelta ? (
        <span
          className={delta >= 0 ? "text-text/positive" : "text-text/negative"}
        >
          {formatDelta(delta)}
        </span>
      ) : null}
    </span>
  );
}

export function useReportingHistoryColumns(
  onEdit?: (snapshot: HistoryRow) => void,
): ColumnDef<HistoryRow>[] {
  return [
    {
      id: "period",
      accessorKey: "periodDate",
      header: m.coInvestments_report_reportingHistory_period(),
      size: 100,
      sortingFn: (rowA, rowB) =>
        compareDatesAsc(rowA.original.periodDate, rowB.original.periodDate),
      cell: ({ getValue }) => (
        <span className="body-base-medium">
          {formatPeriodLabel(getValue() as string)}
        </span>
      ),
    },
    {
      id: "moicGross",
      accessorKey: "moicGross",
      header: m.coInvestments_report_moicGross(),
      cell: ({ row }) => (
        <DeltaCell
          delta={row.original.moicGrossDeltaComputed}
          formatDelta={formatRatioDelta}
          value={formatMoic(row.original.moicGross)}
        />
      ),
    },
    {
      id: "irrNet",
      accessorKey: "irrNet",
      header: m.coInvestments_report_irrNet(),
      cell: ({ row }) => (
        <DeltaCell
          delta={row.original.irrNetDeltaComputed}
          formatDelta={formatIrrDelta}
          value={formatIrr(row.original.irrNet)}
        />
      ),
    },
    {
      id: "dpi",
      accessorKey: "dpi",
      header: m.coInvestments_report_dpi(),
      cell: ({ row }) => (
        <DeltaCell
          delta={row.original.dpiDeltaComputed}
          formatDelta={formatRatioDelta}
          value={formatDpi(row.original.dpi)}
        />
      ),
    },
    {
      id: "tvpi",
      accessorKey: "tvpi",
      header: m.coInvestments_report_tvpi(),
      cell: ({ row }) => (
        <DeltaCell
          delta={row.original.tvpiDeltaComputed}
          formatDelta={formatRatioDelta}
          value={formatTvpi(row.original.tvpi)}
        />
      ),
    },
    {
      id: "note",
      accessorKey: "note",
      enableSorting: false,
      addCellTooltip: false,
      size: 400,
      header: m.coInvestments_report_reportingHistory_note(),
      cell: ({ getValue }) => {
        const note = getValue() as string | undefined;

        return note ? (
          <p className="body-base-regular text-text/secondary whitespace-normal break-words py-2">
            {note}
          </p>
        ) : null;
      },
    },
    ...(onEdit
      ? [
          {
            id: "actions",
            enableSorting: false,
            size: 60,
            cell: ({ row }: CellContext<HistoryRow, unknown>) => (
              <ThemedButton
                ariaLabel={m.coInvestments_report_editEntry()}
                dataTestId="edit-snapshot-button"
                icon={<EditIcon />}
                onClick={() => onEdit(row.original)}
                size="small"
                type="button"
                variant="tertiary"
              />
            ),
          },
        ]
      : []),
  ];
}
